import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  TextField,
  Chip,
  Alert,
  Checkbox,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';
import AddQCSampleModal from '../modals/AddQCSampleModal';
import ExportButton from '../../utils/ExportButton';


// Auth helper function
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    withCredentials: true
  };
};
function QCSamplesPage({ user }) {
  const [samples, setSamples] = useState([]);
  const [summary, setSummary] = useState({ total_samples: 0, total_kgs: 0, this_month_count: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSample, setEditingSample] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filter options from inventory
  const [filterOptions, setFilterOptions] = useState({
    fcl_months: [],
    fcl_numbers: [],
    products: [],
    grades: [],
    brands: [],
    trace_ids: [],
    day_codes: []
  });

  // Filters
  const [filters, setFilters] = useState({
    sample_date: '',
    pallet_id: '',
    fcl_month: '',
    fcl_no: '',
    product: '',
    grade: '',
    brand: '',
    trace_id: '',
    day_code: '',
    sample_bags: '',
    purpose: ''
  });

  useEffect(() => {
    fetchSamples();
    fetchSummary();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    // When FCL month or FCL number changes, update dependent filters
    if (filters.fcl_month || filters.fcl_no) {
      fetchFilterOptions(filters.fcl_month, filters.fcl_no);
    }
  }, [filters.fcl_month, filters.fcl_no]);

  const fetchSamples = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        'https://ccc4cs.choicegroup.in/api/qc_samples.php?action=list',
        getAuthConfig()
      );
      if (response.data.success) {
        setSamples(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch samples');
      }
    } catch (err) {
      console.error('Fetch samples error:', err);
      setError('Error fetching samples. Please check if the table exists.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(
        'https://ccc4cs.choicegroup.in/api/qc_samples.php?action=summary',
        getAuthConfig()
      );
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (err) {
      console.error('Fetch summary error:', err);
    }
  };

  const fetchFilterOptions = async (fcl_month = '', fcl_no = '') => {
    try {
      const params = new URLSearchParams();
      if (fcl_month) params.append('fcl_month', fcl_month);
      if (fcl_no) params.append('fcl_no', fcl_no);

      const response = await axios.get(
        `https://ccc4cs.choicegroup.in/api/qc_samples.php?action=filter_options&${params}`,
        getAuthConfig()
      );
      
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (err) {
      console.error('Fetch filter options error:', err);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredSamples.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredSamples.map(sample => sample.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert('Please select samples to delete');
      return;
    }

    const confirmation = window.confirm(
      `⚠️ WARNING: You are about to delete ${selectedItems.length} QC sample(s).\n\nThis action cannot be undone. Are you sure?`
    );

    if (!confirmation) return;

    try {
      const response = await axios.post(
        'https://ccc4cs.choicegroup.in/api/qc_samples.php',
        { action: 'bulk_delete', ids: selectedItems },
        getAuthConfig()
      );

      if (response.data.success) {
        alert(`✅ Successfully deleted ${response.data.deleted_count} sample(s)`);
        setSelectedItems([]);
        fetchSamples();
        fetchSummary();
      } else {
        alert('❌ ' + response.data.message);
      }
    } catch (err) {
      alert('❌ Error: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sample?')) {
      return;
    }

    try {
      const response = await axios.post(
        'https://ccc4cs.choicegroup.in/api/qc_samples.php',
        { action: 'delete', id },
        getAuthConfig()
      );

      if (response.data.success) {
        fetchSamples();
        fetchSummary();
      } else {
        setError(response.data.message || 'Failed to delete sample');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Error deleting sample');
    }
  };

  // Edit handlers
  const handleEditClick = (sample) => {
    setEditingSample(sample);
    setEditFormData({
      sample_bags: sample.sample_bags || 0,
      sample_date: sample.sample_date || '',
      fcl_month: sample.fcl_month || '',
      fcl_no: sample.fcl_no || '',
      purpose: sample.purpose || 'QC Inspection',
      notes: sample.notes || ''
    });
    setEditError('');
    setEditModalOpen(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    if (!editingSample) return;
    
    const newBags = parseInt(editFormData.sample_bags);
    if (newBags < 0 || isNaN(newBags)) {
      setEditError('Sample bags cannot be negative');
      return;
    }

    if (newBags === 0) {
      if (!window.confirm('Setting bags to 0 will return all bags to inventory. Continue?')) {
        return;
      }
    }

    setEditLoading(true);
    setEditError('');

    try {
      const response = await axios.post(
        'https://ccc4cs.choicegroup.in/api/qc_samples.php',
        {
          action: 'update',
          id: editingSample.id,
          sample_bags: newBags,
          sample_date: editFormData.sample_date,
          fcl_month: editFormData.fcl_month,
          fcl_no: editFormData.fcl_no,
          purpose: editFormData.purpose,
          notes: editFormData.notes
        },
        {
          headers: { 'Content-Type': 'application/json' },
          ...getAuthConfig()
        }
      );

      if (response.data.success) {
        setSuccessMessage(response.data.message || 'Sample updated successfully!');
        setEditModalOpen(false);
        setEditingSample(null);
        fetchSamples();
        fetchSummary();
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setEditError(response.data.message || 'Failed to update sample');
      }
    } catch (err) {
      console.error('Edit error:', err);
      setEditError(err.response?.data?.message || 'Error updating sample');
    } finally {
      setEditLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const getFilteredSamples = () => {
    return samples.filter(sample => {
      if (filters.sample_date && sample.sample_date !== filters.sample_date) return false;
      if (filters.pallet_id && !sample.pallet_id?.toLowerCase().includes(filters.pallet_id.toLowerCase())) return false;
      if (filters.fcl_no && !sample.fcl_no?.toLowerCase().includes(filters.fcl_no.toLowerCase())) return false;
      if (filters.fcl_month && !sample.fcl_month?.toLowerCase().includes(filters.fcl_month.toLowerCase())) return false;
      if (filters.product && !sample.product?.toLowerCase().includes(filters.product.toLowerCase())) return false;
      if (filters.grade && !sample.grade?.toLowerCase().includes(filters.grade.toLowerCase())) return false;
      if (filters.brand && !sample.brand?.toLowerCase().includes(filters.brand.toLowerCase())) return false;
      if (filters.trace_id && !sample.trace_id?.toLowerCase().includes(filters.trace_id.toLowerCase())) return false;
      if (filters.day_code && !sample.day_code?.toLowerCase().includes(filters.day_code.toLowerCase())) return false;
      if (filters.purpose && !sample.purpose?.toLowerCase().includes(filters.purpose.toLowerCase())) return false;
      return true;
    });
  };

  const handleRefresh = () => {
    fetchSamples();
    fetchSummary();
    fetchFilterOptions();
  };

  const handleClearFilters = () => {
    setFilters({
      sample_date: '',
      pallet_id: '',
      fcl_month: '',
      fcl_no: '',
      product: '',
      grade: '',
      brand: '',
      trace_id: '',
      day_code: '',
      sample_bags: '',
      purpose: ''
    });
  };

  const filteredSamples = getFilteredSamples();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">🔬 QC Samples for FCL</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ExportButton 
            exportType="qc_samples" 
            label="Export QC Samples" 
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          {user.role === 'admin' && selectedItems.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
            >
              Delete Selected ({selectedItems.length})
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            Add New Sample
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#2196f3', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle2">Total Samples</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{summary.total_samples || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle2">Total Bags</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{summary.total_bags || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#4caf50', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle2">Total Kgs</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{Number(summary.total_kgs || 0).toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#ff9800', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle2">This Month</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{summary.this_month_count || 0} ({summary.this_month_bags || 0} bags)</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <Button variant="outlined" size="small" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Box>
          <Grid container spacing={2}>
            {/* Row 1: Sample Date, Pallet ID, FCL Month, FCL Number */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Sample Date"
                value={filters.sample_date}
                onChange={(e) => handleFilterChange('sample_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Pallet ID"
                value={filters.pallet_id}
                onChange={(e) => handleFilterChange('pallet_id', e.target.value)}
                placeholder="e.g., 556"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                freeSolo
                options={filterOptions.fcl_months}
                value={filters.fcl_month}
                onChange={(e, newValue) => handleFilterChange('fcl_month', newValue || '')}
                onInputChange={(e, newValue) => handleFilterChange('fcl_month', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="FCL Month"
                    size="small"
                    placeholder="e.g., DEC-2025"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="FCL Number"
                value={filters.fcl_no}
                onChange={(e) => handleFilterChange('fcl_no', e.target.value)}
                placeholder="e.g., 17"
              />
            </Grid>

            {/* Row 2: Product, Grade, Brand, Trace ID */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                freeSolo
                options={filterOptions.products}
                value={filters.product}
                onChange={(e, newValue) => handleFilterChange('product', newValue || '')}
                onInputChange={(e, newValue) => handleFilterChange('product', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product"
                    size="small"
                    placeholder="e.g., CPND"
                  />
                )}
              />
            </Grid>

            {/* Row 2: Grade, Brand, Trace ID, Day Code */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                freeSolo
                options={filterOptions.grades}
                value={filters.grade}
                onChange={(e, newValue) => handleFilterChange('grade', newValue || '')}
                onInputChange={(e, newValue) => handleFilterChange('grade', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Grade"
                    size="small"
                    placeholder="e.g., 31/40"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                freeSolo
                options={filterOptions.brands}
                value={filters.brand}
                onChange={(e, newValue) => handleFilterChange('brand', newValue || '')}
                onInputChange={(e, newValue) => handleFilterChange('brand', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand"
                    size="small"
                    placeholder="e.g., TC"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                freeSolo
                options={filterOptions.trace_ids}
                value={filters.trace_id}
                onChange={(e, newValue) => handleFilterChange('trace_id', newValue || '')}
                onInputChange={(e, newValue) => handleFilterChange('trace_id', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Trace ID"
                    size="small"
                    placeholder="e.g., A1234"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                freeSolo
                options={filterOptions.day_codes}
                value={filters.day_code}
                onChange={(e, newValue) => handleFilterChange('day_code', newValue || '')}
                onInputChange={(e, newValue) => handleFilterChange('day_code', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Day Code"
                    size="small"
                    placeholder="e.g., 5A"
                  />
                )}
              />
            </Grid>

            {/* Row 3: Sample Bags, Purpose */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="No. of Sample Bags"
                value={filters.sample_bags}
                onChange={(e) => handleFilterChange('sample_bags', e.target.value)}
                placeholder="e.g., 5"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Purpose"
                value={filters.purpose}
                onChange={(e) => handleFilterChange('purpose', e.target.value)}
                placeholder="e.g., Quality Check"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {filteredSamples.length} of {samples.length} samples
      </Typography>

      {/* Samples Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#1565c0' }}>
              {user.role === 'admin' && (
                <TableCell padding="checkbox" sx={{ bgcolor: '#1565c0' }}>
                  <Checkbox
                    checked={selectedItems.length === filteredSamples.length && filteredSamples.length > 0}
                    indeterminate={selectedItems.length > 0 && selectedItems.length < filteredSamples.length}
                    onChange={handleSelectAll}
                    sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Pallet ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>FCL No</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>FCL Month</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Sample Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Product</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Grade</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Brand</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Trace ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Day Code</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 700, bgcolor: '#388e3c' }}>Sample Bags</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 700, bgcolor: '#388e3c' }}>Sample Kgs</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Purpose</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Taken By</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Notes</TableCell>
              {user.role === 'admin' && <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={user.role === 'admin' ? 17 : 16} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredSamples.length === 0 ? (
              <TableRow>
                <TableCell colSpan={user.role === 'admin' ? 17 : 16} align="center">
                  No samples found
                </TableCell>
              </TableRow>
            ) : (
              filteredSamples.map((sample) => (
                <TableRow key={sample.id} hover selected={selectedItems.includes(sample.id)}>
                  {user.role === 'admin' && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.includes(sample.id)}
                        onChange={() => handleSelectItem(sample.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{sample.id}</TableCell>
                  <TableCell>
                    <Chip label={sample.pallet_id || '-'} size="small" sx={{ bgcolor: '#e3f2fd', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={sample.fcl_no} size="small" color="primary" />
                  </TableCell>
                  <TableCell>{sample.fcl_month}</TableCell>
                  <TableCell>{new Date(sample.sample_date).toLocaleDateString()}</TableCell>
                  <TableCell>{sample.product || '-'}</TableCell>
                  <TableCell>{sample.grade || '-'}</TableCell>
                  <TableCell>{sample.brand || '-'}</TableCell>
                  <TableCell>{sample.trace_id || '-'}</TableCell>
                  <TableCell>{sample.day_code || '-'}</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#e8f5e9' }}>
                    <strong>{sample.sample_bags || 0}</strong>
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#e8f5e9' }}>
                    <strong>{Number(sample.sample_kgs).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={sample.purpose} 
                      size="small" 
                      sx={{ 
                        bgcolor: sample.purpose === 'QC Inspection' ? '#bbdefb' :
                                 sample.purpose === 'QC Sample' ? '#c8e6c9' :
                                 sample.purpose === 'Kitchen' ? '#ffe0b2' : '#e0e0e0',
                        fontWeight: 500
                      }} 
                    />
                  </TableCell>
                  <TableCell>{sample.taken_by_name || 'Unknown'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {sample.notes || '-'}
                    </Typography>
                  </TableCell>
                  {user.role === 'admin' && (
                    <TableCell align="center">
                      <Tooltip title="Edit Sample">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(sample)}
                          sx={{ mr: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Sample">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(sample.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Sample Modal */}
      <AddQCSampleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          fetchSamples();
          fetchSummary();
        }}
        filterOptions={filterOptions}
      />

      {/* Edit Sample Modal */}
      <Dialog open={editModalOpen} onClose={() => !editLoading && setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 700 }}>
          ✏️ Edit QC Sample #{editingSample?.id}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError('')}>
              {editError}
            </Alert>
          )}
          
          {editingSample && (
            <Box>
              {/* Sample Info - Read Only */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1565c0', mb: 1 }}>
                  📦 Sample Details (Read-only)
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2"><strong>Pallet ID:</strong> {editingSample.pallet_id || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2"><strong>Product:</strong> {editingSample.product}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2"><strong>Grade:</strong> {editingSample.grade}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2"><strong>Brand:</strong> {editingSample.brand}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2"><strong>Trace ID:</strong> {editingSample.trace_id}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2"><strong>Day Code:</strong> {editingSample.day_code}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                      <strong>Current Sample Bags:</strong> {editingSample.sample_bags} ({editingSample.sample_kgs} kg)
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Editable Fields */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Sample Bags"
                    name="sample_bags"
                    value={editFormData.sample_bags}
                    onChange={handleEditFormChange}
                    inputProps={{ min: 0 }}
                    helperText={
                      parseInt(editFormData.sample_bags) === 0 
                        ? "⚠️ Setting to 0 will return all bags to inventory" 
                        : "Changing this will update inventory automatically"
                    }
                    sx={{ 
                      '& .MuiFormHelperText-root': { 
                        color: parseInt(editFormData.sample_bags) === 0 ? '#d32f2f' : '#ff9800',
                        fontWeight: parseInt(editFormData.sample_bags) === 0 ? 700 : 400
                      } 
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e8f5e9', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary">Calculated Sample Weight:</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {(() => {
                        const bags = parseInt(editFormData.sample_bags) || 0;
                        const oldKgs = parseFloat(editingSample.sample_kgs) || 0;
                        const oldBags = parseInt(editingSample.sample_bags) || 1;
                        const weightPerBag = oldBags > 0 ? oldKgs / oldBags : 0.45;
                        return (bags * weightPerBag).toFixed(2);
                      })()} kg
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Sample Date"
                    name="sample_date"
                    value={editFormData.sample_date}
                    onChange={handleEditFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="FCL Month"
                    name="fcl_month"
                    value={editFormData.fcl_month}
                    onChange={handleEditFormChange}
                    placeholder="e.g., DEC-2025"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="FCL Number"
                    name="fcl_no"
                    value={editFormData.fcl_no}
                    onChange={handleEditFormChange}
                    placeholder="e.g., 17"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Purpose"
                    name="purpose"
                    value={editFormData.purpose}
                    onChange={handleEditFormChange}
                    SelectProps={{ native: true }}
                  >
                    <option value="QC Inspection">QC Inspection</option>
                    <option value="QC Sample">QC Sample</option>
                    <option value="Kitchen">Kitchen</option>
                    <option value="Other">Other</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Notes"
                    name="notes"
                    value={editFormData.notes}
                    onChange={handleEditFormChange}
                  />
                </Grid>
              </Grid>

              <Alert severity="warning" sx={{ mt: 2 }}>
                ⚠️ <strong>Important:</strong> If you change the sample bags, the inventory for Pallet {editingSample.pallet_id || 'N/A'} will be automatically adjusted.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => setEditModalOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={editLoading}
            sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
            startIcon={editLoading && <CircularProgress size={20} color="inherit" />}
          >
            {editLoading ? 'Updating...' : '✓ Update Sample'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed', 
            top: 80, 
            right: 20, 
            zIndex: 9999,
            boxShadow: 3
          }}
          onClose={() => setSuccessMessage('')}
        >
          {successMessage}
        </Alert>
      )}
    </Box>
  );
}

export default QCSamplesPage;